// API base origin (no trailing /api). In production the API is served same-origin
// via a Firebase Hosting rewrite (/api/** -> Cloud Run), so the base is empty
// (relative "/api"). In local dev it defaults to the local backend.
//   .env.development -> http://localhost:5001   (npm start)
//   .env.production  -> "" (empty)              (npm run build) => same-origin /api
const _envBase = process.env.REACT_APP_API_URL;
export const API_BASE_URL =
    _envBase !== undefined && _envBase !== null
        ? _envBase // explicit value wins, including "" (same-origin / relative)
        : (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');

// Kept for backward-compat with any other references. Derived from the same base.
export const BACKENDHOST = API_BASE_URL || 'http://localhost';
export const BACKENDPORT = '5001';

// Google OAuth Web client ID. Like BACKENDHOST above, this is hardcoded for
// now; before a non-local deploy, parameterize it via
// process.env.REACT_APP_GOOGLE_CLIENT_ID instead of editing this constant.
export const GOOGLE_CLIENT_ID = '813607726262-vsbvn1qo942iha5u1pjchg8h1sq4fm18.apps.googleusercontent.com';

export const TAGTYPE = ["TOPIC", "SUBJECT", "EXAM", "BCS_TERM", "BOARD", "DIVISION", "UNIT"];
export const CORRECTOPTION = ["A", "B", "C", "D", "E", "F", "G", "H"];
export const QUESTIONDIFFICULTY = ["MEDIUM", "EASY", "HARD"];

// Single source of truth for the gate that decides where the BCS-term UI is
// rendered (question forms, grid filter, public homepage chip, custom quiz).
export const BCS_LEVEL = "BCS";

// Helper for sorting BCS term names ("1st", "10th", "43rd") newest-first.
export const sortBcsTerms = (names) =>
    [...names].sort((a, b) => (parseInt(b, 10) || 0) - (parseInt(a, 10) || 0));

// BCS full model test — the 10 subjects (exact tag-store/tags.json SUBJECT names) and their
// standard 200-question mark distribution. The Auto Test Build "Full Model Test" modal seeds its
// per-subject counts from this and rescales proportionally when the admin changes the total.
export const BCS_MODEL_TEST_DISTRIBUTION = [
    { subject: "বাংলা ভাষা ও সাহিত্য", count: 30 },
    { subject: "English Language and Literature", count: 30 },
    { subject: "বাংলাদেশ বিষয়াবলি", count: 25 },
    { subject: "আন্তর্জাতিক বিষয়াবলি", count: 25 },
    { subject: "ভূগোল (বাংলাদেশ ও বিশ্ব), পরিবেশ ও দুর্যোগ ব্যবস্থাপনা", count: 10 },
    { subject: "সাধারণ বিজ্ঞান", count: 15 },
    { subject: "কম্পিউটার ও তথ্যপ্রযুক্তি", count: 15 },
    { subject: "গাণিতিক যুক্তি", count: 20 },
    { subject: "মানসিক দক্ষতা", count: 15 },
    { subject: "নৈতিকতা, মূল্যবোধ ও সুশাসন", count: 15 },
];
export const BCS_MODEL_TEST_BASE_TOTAL = 200; // the distribution above sums to this
export const BCS_COMPETITION_TOTAL = 100;     // competition: equal share per subject
export const BCS_MINUTES_PER_QUESTION = 0.6;  // full model test default duration = 0.6 × total

// Medical Admission full model test — 5 subjects (exact tag-store/tags.json SUBJECT names) and their
// standard 100-question mark distribution. Same modal behavior as BCS (seed + proportional rescale).
export const MEDICAL_MODEL_TEST_DISTRIBUTION = [
    { subject: "Biology", count: 30 },
    { subject: "Chemistry", count: 25 },
    { subject: "Physics", count: 15 },
    { subject: "English", count: 15 },
    { subject: "General Knowledge", count: 15 },
];
export const MEDICAL_MODEL_TEST_BASE_TOTAL = 100; // the distribution above sums to this
export const MEDICAL_COMPETITION_TOTAL = 100;     // competition: exam distribution, ~100 total
export const MEDICAL_MINUTES_PER_QUESTION = 0.75; // full model test default duration = 0.75 × total

// Dhaka University A-Unit (level VARSITY-DU) full model test — the 4 science subjects (60 MCQ, 15 each).
// Bangla/English are 4th-subject alternatives in the taxonomy but excluded from the model test.
export const DU_MODEL_TEST_DISTRIBUTION = [
    { subject: "Physics", count: 15 },
    { subject: "Chemistry", count: 15 },
    { subject: "Higher Mathematics", count: 15 },
    { subject: "Biology", count: 15 },
];
export const DU_MODEL_TEST_BASE_TOTAL = 60;   // the distribution above sums to this
export const DU_COMPETITION_TOTAL = 60;       // competition: 4 sciences, 15 each
export const DU_MINUTES_PER_QUESTION = 0.75;  // full model test default duration = 0.75 × total (=45 at 60)


export const ROLES = {
    USER: "user",
    ADMIN: "admin",
    SUPER_ADMIN: "super_admin",
};
// export const RANDOMVALUES = ["NO", "YES"]

// ---------- Premium plans / entitlements ----------
// Plan `code`s mirror the backend (apps/subscriptions). Marketing display names
// ("Kobra Starter/Pro/Premium") come from the API `name`; benefit lists come from
// the API `features` — don't hardcode them here.
export const PLAN_CODES = {
    FREE: "FREE",
    STARTER: "STARTER",
    PRO: "PRO",
    PREMIUM: "PREMIUM",
};

export const PLAN_LABELS = {
    [PLAN_CODES.FREE]: "Free",
    [PLAN_CODES.STARTER]: "Kobra Starter",
    [PLAN_CODES.PRO]: "Kobra Pro",
    [PLAN_CODES.PREMIUM]: "Kobra Premium",
};

// Promo/discount code types (super-admin promo management + purchase-flow validation).
export const PROMO_DISCOUNT_TYPES = [
    { value: "PERCENT", label: "Percentage (%)" },
    { value: "FLAT", label: "Fixed amount (৳)" },
];

// Plan tier ordering + the "next tier up" for upgrade nudges (M3 gating).
export const PLAN_RANK = {
    [PLAN_CODES.FREE]: 0,
    [PLAN_CODES.STARTER]: 1,
    [PLAN_CODES.PRO]: 2,
    [PLAN_CODES.PREMIUM]: 3,
};

export const NEXT_PLAN = {
    [PLAN_CODES.FREE]: PLAN_CODES.STARTER,
    [PLAN_CODES.STARTER]: PLAN_CODES.PRO,
    [PLAN_CODES.PRO]: PLAN_CODES.PREMIUM,
    [PLAN_CODES.PREMIUM]: null,
};

// IANA zones for the dual-timezone display convention (see utils/DateUtilities.js).
// Every timestamp shown in the UI renders BD time alongside PST; premium expiry is
// reasoned about in BD time.
export const TIMEZONES = {
    BD: "Asia/Dhaka",
    PST: "America/Los_Angeles",
};

// Sentinel value for "Not Set" filters in the question management page.
// Backend recognizes this exact string and matches rows whose target field is empty.
export const NOT_SET_FILTER = '__not_set__';

// If taglevels are updated, remember to create tags using the json file if needed.
export const TAGLEVELS =  [
    { 
        label: 'SSC',
        value: 'SSC' 
    },
    { 
        label: 'HSC',
        value: 'HSC' 
    },
    {
        label: 'BCS',
        value: 'BCS'
    },
    {
        label: 'GOVT-BANK',
        value: 'GOVT-BANK'
    },
    {
        label: 'PRIMARY-TEACHER',
        value: 'PRIMARY-TEACHER'
    },
    {
        label: 'PRIVATE-BANK',
        value: 'PRIVATE-BANK'
    },
    {
        label: 'NURSING',
        value: 'NURSING'
    },
    {
        label: 'MEDICAL',
        value: 'MEDICAL'
    },
    {
        label: 'BUTEX',
        value: 'BUTEX'
    },
    {
        label: 'IELTS',
        value: 'IELTS'
    },
    {
        label: 'TOEFL',
        value: 'TOEFL'
    },
    {
        label: 'GRE',
        value: 'GRE'
    },
    {
        label: 'SAT',
        value: 'SAT'
    },
    {
        "label": 'VARSITY',
        "value": 'VARSITY',
        submenu: [
            { 
                label: 'VARSITY-DU',
                value: 'VARSITY-DU'
            },
            { 
                label: 'VARSITY-CU',
                value: 'VARSITY-CU'
            },
            { 
                label: 'VARSITY-RU',
                value: 'VARSITY-RU'
            },
            { 
                label: 'VARSITY-JU',
                value: 'VARSITY-JU'
            },
            { 
                label: 'VARSITY-JNU',
                value: 'VARSITY-JNU'
            },
            { 
                label: 'VARSITY-OTHER',
                value: 'VARSITY-OTHER'
            }
        ],
    },
    {
        "label": 'ENGINEERING',
        "value": 'ENGINEERING',
        submenu: [
            { 
                label: 'ENGINEERING-BUET',
                value: 'ENGINEERING-BUET'
            },
            { 
                label: 'ENGINEERING-RUET',
                value: 'ENGINEERING-RUET'
            },
            { 
                label: 'ENGINEERING-KUET',
                value: 'ENGINEERING-KUET'
            },
            { 
                label: 'ENGINEERING-CUET',
                value: 'ENGINEERING-CUET'
            },
            { 
                label: 'ENGINEERING-OTHER',
                value: 'ENGINEERING-OTHER'
            }
        ],
    }
];

export const QUIZTYPE = ["custom", "published", "compitition", "micro", "daily", "previous_year"];
export const QUIZ_VIEW_TYPES = {
    MY_QUIZ: "MY_QUIZ",
    PUBLISHED_QUIZ: "PUBLISHED_QUIZ",
    COMPETITION_QUIZ: "COMPETITION_QUIZ",
    MICRO_QUIZ: "MICRO_QUIZ",
    PREVIOUS_YEAR_QUIZ: "PREVIOUS_YEAR_QUIZ",
    DAILY_CHALLENGE: "DAILY_CHALLENGE",
};

// Friendly tab labels for the quiz-store view switcher (falls back to a de-underscored
// version of the key when a view isn't listed here). The Micro label is level-dependent, so
// use quizViewLabel(view, level) rather than reading this map directly for tabs/headings.
export const QUIZ_VIEW_LABELS = {
    MY_QUIZ: "Create Your Own Model Test",
    PUBLISHED_QUIZ: "Full Published Model Tests",
    COMPETITION_QUIZ: "Competition",
    MICRO_QUIZ: "Micro",
    PREVIOUS_YEAR_QUIZ: "Previous Year",
    DAILY_CHALLENGE: "Daily Challenge",
};

// The Micro view is named by how its quizzes are grouped: IELTS/TOEFL split a model test into
// subjects (Reading/Listening/…) → "Subject-wise"; every other level (SAT/GRE topics, BCS/Medical
// topics) slices per topic → "Topic-wise". Used for tabs, headings and the auto-build buttons.
export const microTestLabel = (level) =>
    level === "IELTS" || level === "TOEFL" ? "Subject-wise Micro Tests" : "Topic-wise Micro Tests";

// Level-aware label for a quiz-store view (tab button + grid heading).
export const quizViewLabel = (view, level) => {
    if (view === QUIZ_VIEW_TYPES.MICRO_QUIZ) return microTestLabel(level);
    return QUIZ_VIEW_LABELS[view] || String(view || "").replace(/_/g, " ");
};

// Quiz-store tabs hidden for a given level. TOEFL is a model-test level: there is no
// user-built "My Quiz" and no previous-year bank — only Published (model tests),
// Competition, Micro (subject/topic practice) and the Daily challenge.
export const QUIZ_VIEWS_HIDDEN_BY_LEVEL = {
    TOEFL: [QUIZ_VIEW_TYPES.MY_QUIZ, QUIZ_VIEW_TYPES.PREVIOUS_YEAR_QUIZ],
    IELTS: [QUIZ_VIEW_TYPES.MY_QUIZ, QUIZ_VIEW_TYPES.PREVIOUS_YEAR_QUIZ],
    SAT: [QUIZ_VIEW_TYPES.MY_QUIZ, QUIZ_VIEW_TYPES.PREVIOUS_YEAR_QUIZ],
    GRE: [QUIZ_VIEW_TYPES.PREVIOUS_YEAR_QUIZ],
};

export function visibleQuizViews(level) {
    const hidden = QUIZ_VIEWS_HIDDEN_BY_LEVEL[level] || [];
    return Object.values(QUIZ_VIEW_TYPES).filter(v => !hidden.includes(v));
}

// Human labels + badge colors per quiz type (admin grid + user surfaces).
export const QUIZ_TYPE_LABELS = {
    custom: "Custom",
    published: "Publish",
    compitition: "Competition",
    micro: "Micro",
    daily: "Daily",
    previous_year: "Previous Year",
};

export const QUIZ_TYPE_COLORS = {
    custom: "info",
    published: "success",
    compitition: "danger",
    micro: "primary",
    daily: "warning",
    previous_year: "dark",
};

// Quiz types whose completed attempts expose retake / submission-details / score / leaderboard.
export const RETAKE_ELIGIBLE_QUIZ_TYPES = ["custom", "daily", "micro", "previous_year", "published"];

// Publish-status filter options for the admin Manage Quizzes grid.
export const PUBLISH_STATUS_FILTER = [
    { value: "", label: "All" },
    { value: "true", label: "Published" },
    { value: "false", label: "Unpublished (Draft)" },
];

// ── Question syllabus lifecycle ──
export const SYLLABUS_STATUS_LABELS = {
    CURRENT: "Current",
    OUTDATED: "Outdated (out of syllabus)",
    WITHDRAWN: "Withdrawn (hidden)",
};

export const SYLLABUS_STATUS_OPTIONS = [
    { value: "CURRENT", label: "Current" },
    { value: "OUTDATED", label: "Outdated" },
    { value: "WITHDRAWN", label: "Withdrawn" },
];

export const SYLLABUS_STATUS_COLORS = {
    CURRENT: "success",
    OUTDATED: "warning",
    WITHDRAWN: "secondary",
};

// Micro quiz: max questions (must match jobprep-api MICRO_MAX_QUESTIONS).
export const MICRO_MAX_QUESTIONS = 10;

export const CORRECTOPTIONTOOPTIONFIELDMAP = {
    "A": "option_a",
    "B": "option_b",
    "C": "option_c",
    "D": "option_d",
    "E": "option_e",
    "F": "option_f",
    "G": "option_g",
    "H": "option_h",
}

export const OPTIONFIELDTOCORRECTOPTIONMAP = {
    "option_a": "A",
    "option_b": "B",
    "option_c": "C",
    "option_d": "D",
    "option_e": "E",
    "option_f": "F",
    "option_g": "G",
    "option_h": "H",
}

// Ordered option field keys (a–h). Single source of truth for iterating options; the
// first 4 are mandatory, E–H are optional extras (blank = absent).
export const OPTION_KEYS = ["option_a", "option_b", "option_c", "option_d", "option_e", "option_f", "option_g", "option_h"];
export const OPTION_BASE_COUNT = 4;
export const OPTION_MAX_COUNT = 8;
// GRE Text Completion (multi-blank) + Sentence Equivalence constraints.
export const TC_MIN_BLANKS = 2;
export const TC_MAX_BLANKS = 3;
export const TC_CHOICES_PER_BLANK = 3;
export const SE_OPTION_COUNT = 6;
export const SE_PICK = 2;

// ---------- New tag dimensions + question modes ----------
// Education boards (SSC/HSC), academic groups (SSC/HSC), and admission units (VARSITY-*).
export const BOARD_VALUES = [
    "Dhaka", "Rajshahi", "Comilla", "Jessore", "Chittagong",
    "Barisal", "Sylhet", "Dinajpur", "Mymensingh",
];
export const DIVISION_VALUES = ["Science", "Arts", "Commerce"];
export const UNIT_VALUES = ["A", "B", "C", "D", "E"];

// Question modes. TEXT collects a free-text answer (graded manually); TEXT_COMPLETION is a
// GRE multi-blank item (2–3 blanks, 3 choices each, one pick per blank); SENTENCE_EQUIVALENCE
// is a GRE 6-option "pick exactly two" item.
export const QUESTION_TYPES = ["MCQ", "TEXT", "TEXT_COMPLETION", "SENTENCE_EQUIVALENCE", "BUILD_SENTENCE", "MATCHING"];
export const QUESTION_TYPE_LABELS = {
    MCQ: "MCQ",
    TEXT: "TEXT (written answer)",
    TEXT_COMPLETION: "GRE Text Completion (multi-blank)",
    SENTENCE_EQUIVALENCE: "GRE Sentence Equivalence (pick 2)",
    BUILD_SENTENCE: "TOEFL Build a Sentence (order tiles)",
    MATCHING: "IELTS Matching (shared option box)",
};
// Roman labels for Text Completion blanks (blank 0 → "(i)", ...).
export const TC_BLANK_LABELS = ["(i)", "(ii)", "(iii)"];

// Language-test levels get side_text + audio; IELTS/TOEFL additionally get a per-question timer.
export const LANGUAGE_LEVELS = ["IELTS", "TOEFL", "GRE"];

// Sectioned/scaled model-test levels — these show the per-section "Model Test Attempts" table on
// the User Statistics page (via the unified /submission/model-test-attempts endpoint) instead of a
// generic per-topic accuracy view. SAT is here but is NOT a LANGUAGE_LEVEL.
export const MODEL_TEST_LEVELS = ["IELTS", "TOEFL", "GRE", "SAT"];

// The "international" exam family (vs. everything else, which is the Bangladeshi family).
// Drives the split "Select Level (Int'l exams)" / "Select Level (BD exams)" navbar controls
// and the home-page exam guide.
export const INTL_LEVEL_VALUES = ["IELTS", "TOEFL", "GRE", "SAT"];
export const isIntlLevel = (level) => INTL_LEVEL_VALUES.includes(level);

// IELTS gap-fill questions answered by TYPING a word-bank letter (A–J). The shared word bank
// (from the passage group) is shown once, read-only, above the blanks for these topics.
export const WORD_BANK_TOPICS = ["Reading-Summary (Word Bank)", "Listening-Flow-chart Completion"];

// IELTS Listening passages can carry a shared map/plan/diagram image (entered on the passage),
// shown once above the blanks for this topic.
export const PASSAGE_IMAGE_TOPICS = ["Listening-Plan/Map/Diagram"];

// Fixed subject lists for the new levels (others come from the tag taxonomy).
export const SUBJECTS_BY_LEVEL = {
    IELTS: ["Reading", "Writing", "Listening", "Speaking"],
    TOEFL: ["Reading", "Writing", "Listening", "Speaking"],
    GRE: ["Math", "Quant", "Essay"],
    SAT: ["Reading and Writing", "Math"],
};

// Level predicates — single source of truth for where each dimension shows.
export const isDivisionLevel = (level) => level === "SSC" || level === "HSC";
export const isBoardLevel = (level) => level === "SSC" || level === "HSC";
export const isUnitLevel = (level) => (level || "").startsWith("VARSITY-");
export const isLanguageLevel = (level) => LANGUAGE_LEVELS.includes(level);
export const isModelTestLevel = (level) => MODEL_TEST_LEVELS.includes(level);
export const isTimerLevel = (level) => level === "IELTS" || level === "TOEFL";
// Essay (GRE) questions don't require options / a correct answer.
export const isEssaySubject = (subject) => (subject || "").trim().toLowerCase() === "essay";

// --- Vocabulary Store ----------------------------------------------------------------
// Exam tabs in the Vocabulary Store. `apiExam` is the value sent to the backend (the IELTS/TOEFL/SAT
// tab maps to the single 'toefl' vocabulary exam — SAT shares that combined pool).
export const VOCAB_EXAM_TABS = [
    { value: "gre", label: "GRE", apiExam: "gre" },
    { value: "ielts_toefl", label: "IELTS/TOEFL/SAT", apiExam: "toefl" },
    { value: "general", label: "General", apiExam: "general" },
];

// GRE book sources.
export const VOCAB_GRE_SOURCES = [
    { value: "barrons", label: "Barron's" },
    { value: "manhattan", label: "Manhattan" },
];

// Every exam tab shows the same three top-level views. `mode` picks the rendered component:
// cards/favorites → word-list + flashcard; practice → reveal-answer MCQ list.
export const VOCAB_VIEWS = [
    { key: "cards", mode: "cards", label: "Your Flash Cards" },
    { key: "skill", mode: "practice", label: "Test Your Skill" },
    { key: "favorites", mode: "favorites", label: "Your Favorites" },
];

// GRE (only) splits each view into two book-source sub-tabs (Barron's / Manhattan). Labels are
// mode-specific; the `source` value flows straight through to the API as ?source=.
export const VOCAB_GRE_SOURCE_TABS = {
    cards: [
        { source: "barrons", label: "Your Barrons Flash Cards" },
        { source: "manhattan", label: "Your Manhattan Flash Cards" },
    ],
    practice: [
        { source: "barrons", label: "Test Your Barron Skill" },
        { source: "manhattan", label: "Test Your Manhattan Skill" },
    ],
    favorites: [
        { source: "barrons", label: "Your Barron Favorites" },
        { source: "manhattan", label: "Your Manhattan Favorites" },
    ],
};

// GRE is the only exam tab that carries book sources (nested sub-tabs).
export const vocabHasSources = (tabValue) => tabValue === "gre";

// A–Z + "All" for the "Go to Word" jump filter.
export const VOCAB_ALPHABET = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));

// --- Micro-quiz grouping ---------------------------------------------------------------
// Micro quizzes are grouped on the listing by a single "group" label — the SECTION the micro
// was split from for model tests, or the TOPIC (subject) for every other level:
//   IELTS / TOEFL -> metadata.split_section   (Reading / Listening / Speaking / Writing)
//   GRE           -> metadata.sections[0].name (the topic, e.g. "Quantitative Reasoning-Algebra")
//   SSC/HSC/BCS/… -> quiz.subject              (the single subject the micro covers)
// Anything without one of these falls under "Other".
export const MODEL_TEST_MICRO_CATEGORIES = ["Reading", "Listening", "Speaking", "Writing"];
export const MICRO_OTHER_GROUP = "Other";

export const microGroupOf = (quiz) => {
    const md = (quiz && quiz.metadata) || {};
    return (
        md.split_section ||
        (Array.isArray(md.sections) && md.sections[0] && md.sections[0].name) ||
        (quiz && quiz.topic) ||
        (quiz && quiz.subject) ||
        MICRO_OTHER_GROUP
    );
};

// Ordered, de-duplicated list of micro group names present in `quizzes`. IELTS/TOEFL keep the
// canonical Reading/Listening/Speaking/Writing order; every other level is sorted alphabetically.
// "Other" is always pushed last.
export const microGroupNames = (quizzes, level) => {
    const present = new Set((quizzes || []).map(microGroupOf));
    const known = level === "IELTS" || level === "TOEFL" ? MODEL_TEST_MICRO_CATEGORIES : [];
    const ordered = known.filter((c) => present.has(c));
    const extras = [...present]
        .filter((c) => !ordered.includes(c) && c !== MICRO_OTHER_GROUP)
        .sort((a, b) => String(a).localeCompare(String(b)));
    const result = [...ordered, ...extras];
    if (present.has(MICRO_OTHER_GROUP)) result.push(MICRO_OTHER_GROUP);
    return result;
};


export const TOPK = 15;
export const itemsPerPageOptions = [5, 10, 25, 50, 100];
export const defaultPageSize = 50;

// Admin passage-authoring screen route.
export const PASSAGE_GROUP_ROUTE = "/passages";

// --- Session inactivity handling ---
// A logged-in user with NO on-page interaction for this long is logged out.
export const SESSION_IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
// Show a "you're about to be logged out" countdown this long before logout.
export const SESSION_WARN_BEFORE_MS = 5 * 60 * 1000; // 5 minutes
// How often the idle poll checks elapsed inactivity.
export const SESSION_ACTIVITY_CHECK_MS = 15 * 1000; // 15 seconds
// localStorage key written on logout to sync logout across browser tabs.
export const SESSION_LOGOUT_EVENT_KEY = 'kobra_session_logout';
export const defaultTotalCount = 0;

// Custom-quiz builder limits. A quiz may hold at most this many questions.
export const MAX_TOTAL_QUESTIONS = 200;
// Sentinel value for the "All Subjects" option in the subject dropdown.
export const ALL_SUBJECTS_OPTION = "__ALL__";

// Anonymous (not-logged-in) daily limits on the public homepage.
export const QUICK_QUIZ_DAILY_LIMIT = 2;
export const PRACTICE_DAILY_LIMIT = 100;
// Anonymous daily practice-session cap (logged-in users use the plan-based server limit).
export const PRACTICE_ANON_LIMIT = 2;

// Notice board types + their flashcard color themes (homepage carousel + admin grid).
export const NOTICE_TYPES = [
    { value: "quiz_result", label: "Test Result" },
    { value: "new_quiz", label: "New Test" },
    { value: "new_quiz_upcoming", label: "New Test — Upcoming" },
    { value: "new_quiz_ongoing", label: "New Test — Ongoing" },
    { value: "generic", label: "Generic" },
];

export const NOTICE_TYPE_COLORS = {
    quiz_result: { bg: "#dcfce7", border: "#22c55e", text: "#065f46" },
    new_quiz: { bg: "#ede9fe", border: "#7c3aed", text: "#4c1d95" },
    new_quiz_upcoming: { bg: "#fef3c7", border: "#f59e0b", text: "#92400e" },
    new_quiz_ongoing: { bg: "#dbeafe", border: "#3b82f6", text: "#1e3a8a" },
    generic: { bg: "#f1f5f9", border: "#94a3b8", text: "#334155" },
};

// Notice-board boxes are colored by LEVEL so all notices for the same level share one color
// (and sit together). Any level string maps deterministically into this palette; global/
// untagged notices ("") use NOTICE_LEVEL_COLOR_DEFAULT.
export const NOTICE_LEVEL_COLOR_PALETTE = [
    { bg: "#ede9fe", border: "#7c3aed", text: "#4c1d95" }, // violet
    { bg: "#dbeafe", border: "#3b82f6", text: "#1e3a8a" }, // blue
    { bg: "#dcfce7", border: "#22c55e", text: "#065f46" }, // green
    { bg: "#fef3c7", border: "#f59e0b", text: "#92400e" }, // amber
    { bg: "#fee2e2", border: "#ef4444", text: "#991b1b" }, // red
    { bg: "#cffafe", border: "#06b6d4", text: "#155e75" }, // cyan
    { bg: "#fce7f3", border: "#ec4899", text: "#9d174d" }, // pink
    { bg: "#ffedd5", border: "#f97316", text: "#9a3412" }, // orange
];
export const NOTICE_LEVEL_COLOR_DEFAULT = { bg: "#f1f5f9", border: "#94a3b8", text: "#334155" };

// Deterministic level -> palette color (stable across renders/sessions for a given level).
export function noticeLevelColor(level) {
    const key = (level || "").trim().toUpperCase();
    if (!key) return NOTICE_LEVEL_COLOR_DEFAULT;
    let hash = 0;
    for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
    return NOTICE_LEVEL_COLOR_PALETTE[hash % NOTICE_LEVEL_COLOR_PALETTE.length];
}

// ---------- Ingestion (CSV question-bank builder) ----------
export const INGESTION_SOURCE_TYPES = ["CSV"];
export const INGESTION_JOB_STATUSES = ["DONE", "FAILED"];
export const INGESTION_DRAFT_STATUSES = ["PENDING_REVIEW", "APPROVED", "REJECTED"];
// Color thresholds for the SimilarQuestionsPanel score badge.
export const SIMILARITY_THRESHOLDS = { DUPLICATE: 0.92, RELATED: 0.7, HIGHLIGHT: 0.8 };
export const INGESTION_DEFAULT_TOP_K = 7;

// ---------- Notifications ----------
// How often the navbar polls /notifications/unread-count for the unread badge.
// Polling pauses while the tab is hidden and re-polls instantly on refocus.
export const NOTIFICATION_POLL_INTERVAL_MS = 45000;

export const NOTIFICATION_TYPE_LABELS = {
    WELCOME: "Welcome",
    PLAN_UPGRADED: "Plan upgraded",
    REFERRAL_CONVERTED: "Referral",
    REWARD_EARNED: "Reward",
    REFUND_ISSUED: "Refund",
    AWARD_SENT: "Award",
    ANNOUNCEMENT: "Announcement",
    GENERIC: "Notification",
};

// ---------- Payments ----------
export const TRANSACTION_TYPE_LABELS = {
    PREMIUM_PURCHASE: "Premium purchase",
    REFERRAL_REWARD: "Referral reward",
    REFUND: "Refund",
    AWARD: "Award",
};

// ---------- Rewards / stars (G2) ----------
export const REWARD_KIND_LABELS = {
    CORRECT_200: "First 200 correct",
    STREAK_7: "7-day streak",
    MONTHLY_TOP1PCT: "Monthly Top 1%",
    ADMIN_GRANT: "Bonus stars",
};

// Time-period filter options for the super-admin rewards grid.
export const REWARD_PERIOD_OPTIONS = [
    { value: "", label: "All time" },
    { value: "current_month", label: "Current month" },
    { value: "last_month", label: "Last month" },
    { value: "last_3_months", label: "Last 3 months" },
    { value: "last_6_months", label: "Last 6 months" },
    { value: "current_year", label: "Current year" },
];

export const TRANSACTION_STATUS_LABELS = {
    PENDING: "Pending",
    SUCCESS: "Success",
    FAILED: "Failed",
    CANCELLED: "Cancelled",
    REFUNDED: "Refunded",
};

export const TRANSACTION_STATUS_COLORS = {
    PENDING: "warning",
    SUCCESS: "success",
    FAILED: "danger",
    CANCELLED: "secondary",
    REFUNDED: "secondary",
};


